import React, { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Paper,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import CustomTextArea from "../../../../../../Components/CustomTextArea/CustomTextArea";
import theme from "../../../../../../Constants/theme";
import { inputStyles } from "../../../../../../Constants/inputStyles";

function AdvancedOptionsDialog({
  displayOptions,
  setDisplayOption,
  localData,
  setLocalData,
  setBotData,
}) {
  // function to update both local and botData in one shot
  const updateBoth = useCallback(
    (fields) => {
      setLocalData((prev) => ({ ...prev, ...fields }));
      setBotData((prev) => ({ ...prev, ...fields }));
    },
    [setLocalData, setBotData]
  );

  // Debounced persisting of the free-text details for "references"
  const saveReferencesText = useMemo(
    () =>
      debounce((text) => {
        updateBoth({ references_text: text });
        localStorage.setItem("references_text", JSON.stringify(text));
      }, 300),
    [updateBoth]
  );
  // Toggle handlers, memoized so children don’t re-render unnecessarily
  const handleReferenceToggle = useCallback(
    (_, newVal) => {
      if (newVal === null) return;
      const checked = newVal === "yes";
      updateBoth({ references: checked });
      localStorage.setItem("references", JSON.stringify(checked));
    },
    [updateBoth]
  );

  const handleSourcesToggle = useCallback(
    (_, newVal) => {
      if (newVal === null) return;
      const checked = newVal === "yes";
      const txt = checked
        ? "\nReturn sources of the documents used in context."
        : "";
      updateBoth({ sources: checked, sources_text: txt });
      localStorage.setItem("sources", JSON.stringify(checked));
      localStorage.setItem("sources_text", JSON.stringify(txt));
    },
    [updateBoth]
  );

  const handleToneToggle = useCallback(
    (_, newVal) => {
      if (newVal === null) return;
      let txt = "";
      if (newVal === "formal") txt = "\nGenerate responses in formal tone.";
      else if (newVal === "informal")
        txt = "\nGenerate responses in informal tone.";
      const toneObj = {
        formal: newVal === "formal",
        informal: newVal === "informal",
        "no-preference": newVal === "no-preference",
      };
      updateBoth({ tone: toneObj, tone_text: txt });
      localStorage.setItem("tone", JSON.stringify(toneObj));
      localStorage.setItem("tone_text", JSON.stringify(txt));
    },
    [updateBoth]
  );

  const handleMemoryToggle = useCallback(
    (_, newVal) => {
      if (newVal === null) return;
      const checked = newVal === "yes";
      updateBoth({ have_memory: checked });
      localStorage.setItem("have_memory", JSON.stringify(checked));
    },
    [updateBoth]
  );

  useEffect(() => {
    const seed = (key, field) => {
      const raw = localStorage.getItem(key);
      if (raw != null) {
        const val = JSON.parse(raw);
        updateBoth({ [field]: val });
      }
    };

    // Booleans
    seed("references", "references");
    seed("sources", "sources");
    seed("have_memory", "have_memory");

    // Free-text
    seed("references_text", "references_text");
    seed("sources_text", "sources_text");
    seed("tone_text", "tone_text");

    // Full tone object (if you stored it)
    seed("tone", "tone");
  }, [updateBoth]);

  // Build a small config array for rendering
  const options = [
    {
      key: "references",
      label: "Block certain content?",
      tooltip:
        "Ensure generated content aligns with responsible and respectful standards.",
      value: localData.references ? "yes" : "no",
      onChange: handleReferenceToggle,
      extraField: localData.references && (
        <Box sx={{ mt: 1 }}>
          <CustomTextArea
            rows={2}
            title="Enter details"
            value={localData.references_text}
            handleChangeInput={(e) => {
              // Update local mirror immediately
              updateBoth({ references_text: e.target.value });
              // Debounced save
              saveReferencesText(e.target.value);
            }}
            onBlur={() => saveReferencesText.flush()}
            customStyles={{
              backgroundColor: theme.inputs.background,
              color: theme.inputs.textColor,
              fontSize: theme.typography.fontSize.xs,
            }}
            LabelStyles={{
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.textColors.dark.primary,
            }}
          />
        </Box>
      ),
      options: ["yes", "no"],
    },
    {
      key: "sources",
      label: "Source citation",
      tooltip:
        "Have responses reference or cite their sources for added reliability?",
      value: localData.sources ? "yes" : "no",
      onChange: handleSourcesToggle,
      options: ["yes", "no"],
    },
    {
      key: "tone",
      label: "Response tone",
      tooltip:
        "Formal for professional, Informal for casual, or No Preference.",
      value:
        Object.keys(localData.tone || {}).find((k) => localData.tone[k]) ||
        "no-preference",
      onChange: handleToneToggle,
      options: ["no-preference", "formal", "informal"],
    },
    {
      key: "have_memory",
      label: "Follow-up questions",
      tooltip: "Enable multi-turn dialogues for deeper topic exploration.",
      value: localData.have_memory ? "yes" : "no",
      onChange: handleMemoryToggle,
      options: ["yes", "no"],
    },
  ];

  return (
    <Dialog
      open={displayOptions}
      fullWidth
      onClose={() => setDisplayOption(false)}
      PaperProps={{ sx: dialogStyles.dialogRoot }}
    >
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Typography variant="h6" sx={{ color: theme.textColors.dark.primary }}>
          Advanced Options
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
        <DialogContentText sx={dialogStyles.dialogContentText}>
          The information provided below will be included in the prompt.
        </DialogContentText>

        <Paper
          sx={{
            ...dialogStyles.paperSection,
            maxHeight: "none",
            overflowY: "visible",
            height: "auto",
          }}
        >
          {options.map(
            ({ key, label, tooltip, value, onChange, options, extraField }) => (
              <Box
                key={key}
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      color: theme.textColors.dark.primary,
                    }}
                  >
                    {label}
                  </Typography>
                  <Tooltip title={tooltip} arrow>
                    <InfoOutlined
                      fontSize="small"
                      sx={{ color: theme.textColors.dark.secondary }}
                    />
                  </Tooltip>
                </Box>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={value}
                  onChange={onChange}
                >
                  {options.map((opt) => (
                    <ToggleButton
                      key={opt}
                      value={opt}
                      sx={inputStyles.toggleButtonStyles}
                    >
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {extraField}
              </Box>
            )
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
}

export default AdvancedOptionsDialog;
